import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface Banner {
  id: number;
  titulo: string;
  subtitulo?: string;
  imagem?: string;
  link?: string;
  acao?: string;
  acaoId?: string;
  corFundo?: string;
}

interface BannerCarouselProps {
  banners: Banner[];
  autoPlayInterval?: number;
}

const BannerCarousel: React.FC<BannerCarouselProps> = ({ banners, autoPlayInterval = 5000 }) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const goTo = useCallback((index: number) => {
    setCurrent(index);
    if (scrollRef.current) {
      const child = scrollRef.current.children[index] as HTMLElement;
      if (child) {
        scrollRef.current.scrollTo({ left: child.offsetLeft, behavior: 'smooth' });
      }
    }
  }, []);

  // Auto-play
  useEffect(() => {
    if (paused || banners.length <= 1) return;
    const timer = setInterval(() => {
      goTo((current + 1) % banners.length);
    }, autoPlayInterval);
    return () => clearInterval(timer);
  }, [current, paused, banners.length, autoPlayInterval, goTo]);

  const handleClick = (banner: Banner) => {
    if (banner.acao === 'restaurant' && banner.acaoId) {
      navigate(`/food/restaurant/${banner.acaoId}`);
    } else if (banner.acao === 'category' && banner.acaoId) {
      navigate(`/food?category=${banner.acaoId}`);
    } else if (banner.link) {
      window.open(banner.link, '_blank');
    }
  };

  if (banners.length === 0) return null;

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setTimeout(() => setPaused(false), 3000)}
    >
      {/* Slides */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-3"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className="snap-center flex-shrink-0 w-full cursor-pointer"
            onClick={() => handleClick(banner)}
          >
            <div
              className="relative rounded-2xl overflow-hidden h-40 sm:h-48"
              style={{
                background: banner.imagem ? undefined : (banner.corFundo || 'linear-gradient(135deg, #FF6B35, #F7C948)'),
              }}
            >
              {/* Background Image */}
              {banner.imagem && (
                <img
                  src={banner.imagem}
                  alt={banner.titulo}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />

              {/* Content */}
              <div className="relative h-full flex flex-col justify-end p-5">
                <h3 className="text-white text-lg sm:text-xl font-bold drop-shadow-lg">
                  {banner.titulo}
                </h3>
                {banner.subtitulo && (
                  <p className="text-white/90 text-sm mt-1 drop-shadow">
                    {banner.subtitulo}
                  </p>
                )}
              </div>

              {/* Gradient border bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-1"
                style={{ background: banner.corFundo || 'linear-gradient(90deg, #FF6B35, #F7C948)' }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Dot indicators */}
      {banners.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`rounded-full transition-all duration-300 ${
                index === current
                  ? 'w-6 h-2 bg-orange-500'
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerCarousel;
